import type { Bulletin } from '@shared/types/Bulletin';
import type { Salarie } from '@shared/types/Salarie';
import type { Tenant } from '@shared/types/Tenant';
import {
  PLAFOND_CNAMGS,
  TAUX_CNAMGS_EMP,
  TAUX_CNAMGS_SAL,
  TAUX_CNSS_EMP,
  TAUX_CNSS_VID_SAL,
} from '@shared/constants/Taux';
import { roundFCFA } from '@shared/utils/round';
import {
  calculerDeductionAf,
  PARAMETRES_DTS_PAR_DEFAUT,
  type ParametresDts,
} from '@domain/fiscalite/DtsParametres';
import {
  libelleTrimestre,
  parseTrimestre,
  periodesDuTrimestre,
  trimestreDePeriode,
} from '@domain/fiscalite/DtsService';

/** Taux global CNSS du modèle DTS (salarié 5 % + employeur 18 % — Excel / décret 0487). */
export const TAUX_DTS_CNSS = arrondiTaux(TAUX_CNSS_VID_SAL + TAUX_CNSS_EMP);
/** Taux global CNAMGS du modèle DTS (salarié 2 % + employeur 4,1 %). */
export const TAUX_DTS_CNAMGS = arrondiTaux(TAUX_CNAMGS_SAL + TAUX_CNAMGS_EMP);

/** Évite les artefacts flottants (0,05 + 0,18 = 0,22999999999999998) dans les cellules de taux. */
function arrondiTaux(taux: number): number {
  return Math.round(taux * 1e6) / 1e6;
}

const MOIS_DTS = [
  ['JANVIER', 'FEVRIER', 'MARS'],
  ['AVRIL', 'MAI', 'JUIN'],
  ['JUILLET', 'AOUT', 'SEPTEMBRE'],
  ['OCTOBRE', 'NOVEMBRE', 'DECEMBRE'],
] as const;

const ECHEANCE_DTS = [
  { jour: 30, mois: 3, libelleMois: 'AVRIL' },
  { jour: 31, mois: 6, libelleMois: 'JUILLET' },
  { jour: 31, mois: 9, libelleMois: 'OCTOBRE' },
  { jour: 31, mois: 0, libelleMois: 'JANVIER' },
] as const;

export interface DtsMoisOfficiel {
  periode: string;
  salaire_plafonne_cnss: number | null;
  salaire_deplafonne: number | null;
  assiette_cnamgs: number | null;
  jours: number | null;
  cnss_patronale: number | null;
  cnss_salariale: number | null;
  cnamgs_patronale: number | null;
  cnamgs_salariale: number | null;
}

export interface DtsSalarieOfficiel {
  numero: number;
  numero_cnss: string;
  numero_cnamgs: string;
  numero_paie: string;
  nom_prenom: string;
  taux_cnss_pct: number;
  embauche: string;
  cessation: string;
  mois: [DtsMoisOfficiel, DtsMoisOfficiel, DtsMoisOfficiel];
}

export interface DtsEmployeurOfficiel {
  matricule_cnss: string;
  matricule_cnamgs: string;
  raison_sociale: string;
  sigle: string;
  bp: string;
  ville: string;
  telephone: string;
  fax: string;
  email: string;
  nif: string;
  rccm: string;
}

export interface DtsOfficielAgrege {
  trimestre: string;
  annee: number;
  numero_trimestre: 1 | 2 | 3 | 4;
  periodes: [string, string, string];
  libelles_mois: readonly [string, string, string];
  regime: string;
  employeur: DtsEmployeurOfficiel;
  salaries: DtsSalarieOfficiel[];
  effectif: number;
  masse_cnss: number;
  masse_cnamgs: number;
  cotisation_cnss: number;
  cotisation_cnss_patronale: number;
  cotisation_cnss_salariale: number;
  cotisation_cnamgs: number;
  cotisation_cnamgs_patronale: number;
  cotisation_cnamgs_salariale: number;
  cotisation_globale: number;
  /** DEC-15 : Avis D26 (allocations familiales), D27 (ristourne), D28 (total déduit). */
  allocations_familiales: number;
  ristourne_af: number;
  deduction_af: number;
  /** Paramètres nommés appliqués (DEC-03 / DEC-15) : voir `DtsParametres`. */
  parametres: ParametresDts;
  date_limite_depot: string;
  date_limite_depot_long: string;
  plafond_cnamgs: number;
  taux_cnss: number;
  taux_cnamgs: number;
  taux_cnamgs_patronal: number;
  taux_cnamgs_salarial: number;
  reference_depot: string;
}

function bulletinEligible(b: Bulletin): boolean {
  return b.statut === 'valide' || b.statut === 'paye';
}

function numeroCnss(salarie: Salarie | undefined): string {
  if (!salarie) return '';
  return (salarie.numero_cnss || salarie.cnss || '').trim();
}

function nomPrenom(b: Bulletin, salarie: Salarie | undefined): string {
  if (salarie) {
    const compose = `${salarie.nom} ${salarie.prenom}`.trim();
    if (compose) return compose.toUpperCase();
  }
  return (b.nom_complet || b.matricule || '').toUpperCase();
}

export function formatDateDts(iso?: string): string {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso.trim());
  if (!m) return iso;
  return `${m[3]}/${m[2]}/${m[1]}`;
}

export function joursCalendaires(periode: string): number {
  const [annee, mois] = periode.split('-').map(Number);
  if (!annee || !mois) return 30;
  return new Date(annee, mois, 0).getDate();
}

function salaireDeplafonne(b: Bulletin): number {
  const brut = b.brut_total || b.brut_imposable || 0;
  return roundFCFA(Math.max(brut, b.base_cnss || 0));
}

/**
 * Assiette CNAMGS du mois. DEC-03 option A : celle du bulletin (déjà plafonnée par salarié et par
 * mois). Option B : non plafonnée — voir l'approximation décrite dans `DtsParametres`.
 */
function assietteCnamgs(b: Bulletin, parametres: ParametresDts): number {
  const base = roundFCFA(b.base_cnamgs || 0);
  if (parametres.plafondCnamgs.option !== 'B' || base < PLAFOND_CNAMGS) return base;
  return roundFCFA(Math.max(base, b.brut_imposable || 0));
}

function moisDepuisBulletin(
  b: Bulletin | undefined,
  periode: string,
  parametres: ParametresDts,
): DtsMoisOfficiel {
  if (!b) {
    return {
      periode,
      salaire_plafonne_cnss: null,
      salaire_deplafonne: null,
      assiette_cnamgs: null,
      jours: null,
      cnss_patronale: null,
      cnss_salariale: null,
      cnamgs_patronale: null,
      cnamgs_salariale: null,
    };
  }
  return {
    periode,
    salaire_plafonne_cnss: roundFCFA(b.base_cnss || 0),
    salaire_deplafonne: salaireDeplafonne(b),
    assiette_cnamgs: assietteCnamgs(b, parametres),
    jours: b.jours_travailles && b.jours_travailles > 0 ? b.jours_travailles : joursCalendaires(periode),
    cnss_patronale: roundFCFA((b.cnss_pf_emp || 0) + (b.cnss_rp_emp || 0) + (b.cnss_vid_emp || 0)),
    cnss_salariale: roundFCFA(b.cnss_vid_sal || 0),
    cnamgs_patronale: roundFCFA((b.cnamgs_emp || 0) + (b.cnamgs_fam_emp || 0)),
    cnamgs_salariale: roundFCFA(b.cnamgs_sal || 0),
  };
}

function dateLimite(annee: number, trimestre: 1 | 2 | 3 | 4): { iso: string; libelle: string } {
  const spec = ECHEANCE_DTS[trimestre - 1]!;
  const y = trimestre === 4 ? annee + 1 : annee;
  const iso = `${y}-${String(spec.mois + 1).padStart(2, '0')}-${String(spec.jour).padStart(2, '0')}`;
  return { iso, libelle: `${spec.jour} ${spec.libelleMois} ${y}` };
}

function employeurDepuis(entreprise?: Tenant | null): DtsEmployeurOfficiel {
  const p = entreprise?.parametres;
  return {
    matricule_cnss: (p?.numero_cnss_employeur || '').trim(),
    matricule_cnamgs: (p?.numero_cnamgs_employeur || '').trim(),
    raison_sociale: (entreprise?.raison_sociale || entreprise?.nom || '').trim(),
    sigle: (p?.trade_name || '').trim(),
    bp: (entreprise?.adresse || '').trim(),
    ville: (entreprise?.ville || '').trim(),
    telephone: (entreprise?.telephone || '').trim(),
    fax: '',
    email: (entreprise?.email || '').trim(),
    nif: (entreprise?.nif || '').trim(),
    rccm: (entreprise?.rccm || '').trim(),
  };
}

function cessationDansTrimestre(
  salarie: Salarie | undefined,
  periodes: readonly string[],
): string {
  const sortie = salarie?.date_sortie?.trim();
  if (!sortie) return '';
  const dernier = periodes[2] ?? '';
  const finTrimestre = `${dernier}-${String(joursCalendaires(dernier)).padStart(2, '0')}`;
  if (sortie <= finTrimestre) return formatDateDts(sortie);
  return '';
}

/**
 * Agrège les bulletins validés/payés du trimestre au format des DTS officielles
 * CNSS (`018-…-Q nTAAAA.xls`) et CNAMGS (`DTS_AAAA-Tn_matricule.xlsx`).
 */
export class DtsOfficiel {
  static trimestreDePeriode = trimestreDePeriode;
  static libelleTrimestre = libelleTrimestre;
  static periodesDuTrimestre = periodesDuTrimestre;

  static agreger(
    bulletins: readonly Bulletin[],
    salaries: readonly Salarie[],
    trimestre: string,
    entreprise?: Tenant | null,
    referenceDepot = '',
    parametres: ParametresDts = PARAMETRES_DTS_PAR_DEFAUT,
  ): DtsOfficielAgrege {
    const { annee, trimestre: numero } = parseTrimestre(trimestre);
    const periodes = periodesDuTrimestre(trimestre);
    const dansTrimestre = new Set(periodes);
    const retenus = bulletins.filter((b) => dansTrimestre.has(b.periode) && bulletinEligible(b));
    if (retenus.length === 0) {
      throw new Error(`Aucun bulletin validé ou payé sur ${libelleTrimestre(trimestre)}`);
    }

    const parId = new Map(salaries.map((s) => [s.id, s]));
    const parSalarie = new Map<string, Bulletin[]>();
    for (const b of retenus) {
      const list = parSalarie.get(b.salarie_id) ?? [];
      list.push(b);
      parSalarie.set(b.salarie_id, list);
    }

    const salariesLignes = Array.from(parSalarie.entries())
      .map(([id, list]) => {
        const premier = list[0]!;
        const sal = parId.get(id);
        return {
          matricule: premier.matricule || sal?.matricule || '',
          salarie: sal,
          bulletins: list,
          nom: nomPrenom(premier, sal),
        };
      })
      .sort((a, b) => a.matricule.localeCompare(b.matricule, 'fr'));

    const lignes: DtsSalarieOfficiel[] = salariesLignes.map((entry, index) => {
      const parMois = new Map(entry.bulletins.map((b) => [b.periode, b]));
      const mois = periodes.map((p) => moisDepuisBulletin(parMois.get(p), p, parametres)) as [
        DtsMoisOfficiel,
        DtsMoisOfficiel,
        DtsMoisOfficiel,
      ];
      return {
        numero: index + 1,
        numero_cnss: numeroCnss(entry.salarie),
        numero_cnamgs: (entry.salarie?.cnamgs || '').trim(),
        numero_paie: entry.matricule,
        nom_prenom: entry.nom,
        taux_cnss_pct: roundFCFA(TAUX_DTS_CNSS * 100),
        embauche: formatDateDts(entry.salarie?.date_embauche),
        cessation: cessationDansTrimestre(entry.salarie, periodes),
        mois,
      };
    });

    let masseCnss = 0;
    let masseCnamgs = 0;
    let cotCnssPat = 0;
    let cotCnssSal = 0;
    let cotCnamgsPat = 0;
    let cotCnamgsSal = 0;
    for (const l of lignes) {
      for (const m of l.mois) {
        masseCnss += m.salaire_plafonne_cnss ?? 0;
        masseCnamgs += m.assiette_cnamgs ?? 0;
        cotCnssPat += m.cnss_patronale ?? 0;
        cotCnssSal += m.cnss_salariale ?? 0;
        cotCnamgsPat += m.cnamgs_patronale ?? 0;
        cotCnamgsSal += m.cnamgs_salariale ?? 0;
      }
    }
    masseCnss = roundFCFA(masseCnss);
    masseCnamgs = roundFCFA(masseCnamgs);
    cotCnssPat = roundFCFA(cotCnssPat);
    cotCnssSal = roundFCFA(cotCnssSal);
    cotCnamgsPat = roundFCFA(cotCnamgsPat);
    cotCnamgsSal = roundFCFA(cotCnamgsSal);
    const cotCnss = roundFCFA(cotCnssPat + cotCnssSal);
    const cotCnamgs = roundFCFA(cotCnamgsPat + cotCnamgsSal);
    const echeance = dateLimite(annee, numero);
    const deduction = calculerDeductionAf(masseCnss, parametres.ristourneAf);

    return {
      trimestre,
      annee,
      numero_trimestre: numero,
      periodes,
      libelles_mois: MOIS_DTS[numero - 1]!,
      regime: 'G',
      employeur: employeurDepuis(entreprise),
      salaries: lignes,
      effectif: lignes.length,
      masse_cnss: masseCnss,
      masse_cnamgs: masseCnamgs,
      cotisation_cnss: cotCnss,
      cotisation_cnss_patronale: cotCnssPat,
      cotisation_cnss_salariale: cotCnssSal,
      cotisation_cnamgs: cotCnamgs,
      cotisation_cnamgs_patronale: cotCnamgsPat,
      cotisation_cnamgs_salariale: cotCnamgsSal,
      cotisation_globale: roundFCFA(cotCnss + cotCnamgs),
      allocations_familiales: deduction.allocations_familiales,
      ristourne_af: deduction.ristourne_af,
      deduction_af: deduction.deduction_af,
      parametres,
      date_limite_depot: echeance.iso,
      date_limite_depot_long: echeance.libelle,
      /** 0 = non plafonné (évite Infinity dans Excel DTS). */
      plafond_cnamgs: Number.isFinite(PLAFOND_CNAMGS) ? PLAFOND_CNAMGS : 0,
      taux_cnss: TAUX_DTS_CNSS,
      taux_cnamgs: TAUX_DTS_CNAMGS,
      taux_cnamgs_patronal: TAUX_CNAMGS_EMP,
      taux_cnamgs_salarial: TAUX_CNAMGS_SAL,
      reference_depot: referenceDepot.trim(),
    };
  }

  static nomFichierCnss(agrege: DtsOfficielAgrege): string {
    const matricule = sanitizeNomFichier(agrege.employeur.matricule_cnss || 'DTS-CNSS');
    return `${matricule} ${agrege.numero_trimestre}T${agrege.annee}.xlsx`;
  }

  static nomFichierCnamgs(agrege: DtsOfficielAgrege): string {
    const matricule = sanitizeNomFichier(agrege.employeur.matricule_cnamgs || 'matriculeemployeur');
    return `DTS_${agrege.annee}-T${agrege.numero_trimestre}_${matricule}.xlsx`;
  }
}

function sanitizeNomFichier(value: string): string {
  return value.replace(/[\\/:*?"<>|]/g, '-').trim() || 'employeur';
}
